import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useAuth } from '@clerk/clerk-react'

// Core pages - loaded immediately (most common routes)
import HomePage1 from './pages/HomePage1'
import DashboardPage from './pages/DashboardPage'
import SignInPage from './pages/SignInPage'
import SignUpPage from './pages/SignUpPage'
import AstroRedirect from './components/AstroRedirect'

// Lazy loaded pages - loaded on demand
const WelcomeResultsPage = lazy(() => import('./pages/WelcomeResultsPage'))
const WelcomePutLivePage = lazy(() => import('./pages/WelcomePutLivePage'))
const WelcomeIntentPage = lazy(() => import('./pages/WelcomeIntentPage'))
const WelcomeStartPage = lazy(() => import('./pages/WelcomeStartPage'))
const WelcomeReadingPage = lazy(() => import('./pages/WelcomeReadingPage'))
const PublicScanPage = lazy(() => import('./pages/PublicScanPage'))
const OnboardingClaimPage = lazy(() => import('./pages/OnboardingClaimPage'))
const SubscriptionCheckoutPage = lazy(() => import('./pages/SubscriptionCheckoutPage'))
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'))
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'))
const CookiesPage = lazy(() => import('./pages/CookiesPage'))
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const AIAuditLandingPage = lazy(() => import('./pages/AIAuditLandingPage'))
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'))
const AROPage = lazy(() => import('./pages/AROPage'))
const AgencyPage = lazy(() => import('./pages/AgencyPage'))
const ManagedPage = lazy(() => import('./pages/ManagedPage'))
const WhatAIAgentsSeePage = lazy(() => import('./pages/WhatAIAgentsSeePage'))
const AgentReadinessCoursePage = lazy(() => import('./pages/AgentReadinessCoursePage'))
const TeamWorkflowPage = lazy(() => import('./pages/TeamWorkflowPage'))
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'))
const HubSpotAeoPage = lazy(() => import('./pages/HubSpotAeoPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const CareersPage = lazy(() => import('./pages/CareersPage'))
const CustomersPage = lazy(() => import('./pages/CustomersPage'))
const PartnersPage = lazy(() => import('./pages/PartnersPage'))
const PressPage = lazy(() => import('./pages/PressPage'))
const SecurityPage = lazy(() => import('./pages/SecurityPage'))
const SitemapPage = lazy(() => import('./pages/SitemapPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const SolutionIndustryPage = lazy(() => import('./pages/SolutionIndustryPage'))
const DesignSystemPage = lazy(() => import('./pages/design-system/DesignSystemPage'))
const UiKitPage = lazy(() => import('./pages/design-system/UiKitPage'))
const SpecimenPage = lazy(() => import('./pages/design-system/SpecimenPage'))

// Lazy loaded - Settings Pages
const SettingsLayout = lazy(() => import('./components/settings/SettingsLayout'))
const AccountSettings = lazy(() => import('./pages/settings/AccountSettings'))
const BillingSettings = lazy(() => import('./pages/settings/BillingSettings'))
const IntegrationsSettings = lazy(() => import('./pages/settings/IntegrationsSettings'))
const PreferencesSettings = lazy(() => import('./pages/settings/PreferencesSettings'))
const AdvancedSettings = lazy(() => import('./pages/settings/AdvancedSettings'))
const TeamSettings = lazy(() => import('./pages/settings/TeamSettings'))
const SubscribeCheckoutPage = lazy(() => import('./pages/settings/SubscribeCheckoutPage'))

// Lazy loaded - Admin Pages
const AdminLayout = lazy(() => import('./components/AdminLayout'))
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'))
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'))
const AdminMonitoring = lazy(() => import('./pages/admin/AdminMonitoring'))
const AdminTickets = lazy(() => import('./pages/admin/AdminTickets'))
const AdminContent = lazy(() => import('./pages/admin/AdminContent'))
const AdminPrivateBetaRequests = lazy(() => import('./pages/admin/AdminPrivateBetaRequests'))
const AdminChiefBeta = lazy(() => import('./pages/admin/AdminChiefBeta'))
const AdminRoadmap = lazy(() => import('./pages/admin/AdminRoadmap'))
const AdminGrowth = lazy(() => import('./pages/admin/AdminGrowth'))
const AdminCampaigns = lazy(() => import('./pages/admin/AdminCampaigns'))
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'))
const AdminEmails = lazy(() => import('./pages/admin/AdminEmails'))
const AdminRevenue = lazy(() => import('./pages/admin/AdminRevenue'))
const AdminAudits = lazy(() => import('./pages/admin/AdminAudits'))
const AdminDemoRequests = lazy(() => import('./pages/admin/AdminDemoRequests'))
const AdminAgencyApplications = lazy(() => import('./pages/admin/AdminAgencyApplications'))
const AdminManagedApplications = lazy(() => import('./pages/admin/AdminManagedApplications'))
const AdminCancellations = lazy(() => import('./pages/admin/AdminCancellations'))

// Lazy loaded - Public Resource Pages
const ResourcesListingPage = lazy(() => import('./pages/ResourcesListingPage'))
const ResourcePostPage = lazy(() => import('./pages/ResourcePostPage'))
const LearnPage = lazy(() => import('./pages/LearnPage'))
const LearnVideoPage = lazy(() => import('./pages/LearnVideoPage'))
const LearnCategoryPage = lazy(() => import('./pages/LearnCategoryPage'))

// Lazy loaded - Feature Pages
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'))
const HubSpotPage = lazy(() => import('./pages/HubSpotPage'))
const HubSpotCallbackPage = lazy(() => import('./pages/HubSpotCallbackPage'))
const HubSpotConnectStartPage = lazy(() => import('./pages/HubSpotConnectStartPage'))
const HubSpotInstallPage = lazy(() => import('./pages/HubSpotInstallPage'))
const ChiefOfStaffPage = lazy(() => import('./pages/ChiefOfStaffPage'))
const RecommendationReportPage = lazy(() => import('./pages/RecommendationReportPage'))
const DomainDetailPage = lazy(() => import('./pages/DomainDetailPage'))
const DomainAIReportPage = lazy(() => import('./pages/DomainAIReportPage'))
const DomainBotTrackingPage = lazy(() => import('./pages/DomainBotTrackingPage'))
const DomainGeneratePage = lazy(() => import('./pages/DomainGeneratePage'))
const MonitorSetupPage = lazy(() => import('./pages/MonitorSetupPage'))
const MonitoringPickerPage = lazy(() => import('./pages/MonitoringPickerPage'))
const DomainMonitoringPage = lazy(() => import('./pages/DomainMonitoringPage'))
const DomainSettingsPage = lazy(() => import('./pages/DomainSettingsPage'))
const UnifiedPageDetail = lazy(() => import('./pages/UnifiedPageDetail'))
const PageDetailPage = lazy(() => import('./pages/PageDetailPage'))
const WhatsNewPage = lazy(() => import('./pages/WhatsNewPage'))
const TeamSettingsLayout = lazy(() => import('./components/team/TeamSettingsLayout'))
const TeamOverviewPage = lazy(() => import('./pages/team/TeamOverviewPage'))
const TeamMembersPage = lazy(() => import('./pages/team/TeamMembersPage'))
const InvitationsPage = lazy(() => import('./pages/team/InvitationsPage'))
const JoinTeamPage = lazy(() => import('./pages/JoinTeamPage'))

// Lazy loaded - Free Schema Generator Pages
const FreeSchemaGeneratorPage = lazy(() => import('./pages/FreeSchemaGeneratorPage'))
const FAQSchemaGeneratorPage = lazy(() => import('./pages/FAQSchemaGeneratorPage'))
const ArticleSchemaGeneratorPage = lazy(() => import('./pages/ArticleSchemaGeneratorPage'))
const BlogPostingSchemaGeneratorPage = lazy(() => import('./pages/BlogPostingSchemaGeneratorPage'))
const HowToSchemaGeneratorPage = lazy(() => import('./pages/HowToSchemaGeneratorPage'))
const ProductSchemaGeneratorPage = lazy(() => import('./pages/ProductSchemaGeneratorPage'))
const LocalBusinessSchemaGeneratorPage = lazy(() => import('./pages/LocalBusinessSchemaGeneratorPage'))
const OrganizationSchemaGeneratorPage = lazy(() => import('./pages/OrganizationSchemaGeneratorPage'))
const EventSchemaGeneratorPage = lazy(() => import('./pages/EventSchemaGeneratorPage'))
const ReviewSchemaGeneratorPage = lazy(() => import('./pages/ReviewSchemaGeneratorPage'))
const BreadcrumbSchemaGeneratorPage = lazy(() => import('./pages/BreadcrumbSchemaGeneratorPage'))
const ServiceSchemaGeneratorPage = lazy(() => import('./pages/ServiceSchemaGeneratorPage'))
const SchemaMarkupGraderPage = lazy(() => import('./pages/SchemaMarkupGraderPage'))

// Lazy loaded - Pillar/Content Pages
const HowToWinFeaturedSnippetsPage = lazy(() => import('./pages/HowToWinFeaturedSnippetsPage'))
const JsonLdVsMicrodataPage = lazy(() => import('./pages/JsonLdVsMicrodataPage'))
const HowAIEnginesRankSourcesPage = lazy(() => import('./pages/HowAIEnginesRankSourcesPage'))
const EntityBasedSeoStrategiesPage = lazy(() => import('./pages/EntityBasedSeoStrategiesPage'))

// Lazy loaded - Utility Components
const ModelTester = lazy(() => import('./components/ModelTester'))

// Components (always loaded - small/essential)
import ScrollToTop from './components/ScrollToTop'
import { AuthLoadingScreen } from './components/AuthLoadingScreen'
import { PublicLayout } from './components/PublicLayout'
import { PublicScanPrepOverlay } from './components/onboarding/PublicScanPrepScreen'
import NotFoundPage from './pages/NotFoundPage'
import './design-system/feedback.css'
import ChunkErrorBoundary from './components/ChunkErrorBoundary'
import NetworkTroubleshootingModal from './components/NetworkTroubleshootingModal'
import { ServiceRecoveryToast } from './components/ServiceStatusBanner'

// Loading fallback for lazy components
const PageLoadingFallback = () => (
  window.location.pathname.startsWith('/scan/') ? (
    /* The overlay paints its own ground and covers the window, which is what
       this wrapper was doing by hand in the old palette. */
    <PublicScanPrepOverlay />
  ) : (
    /* The same screen Clerk's wait draws, in the same place. This boundary
       sits above every layout, so while a route's code is on its way there
       is no shell on screen: the mark stays where it was a moment ago
       instead of giving way to a bare ring near the top. */
    <AuthLoadingScreen />
  )
)

// Layout
import Layout from './components/Layout'
import DefaultSEO from './components/DefaultSEO'

// Contexts
import { OnboardingProvider } from './contexts/OnboardingContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { TeamProvider } from './contexts/TeamContext'
import { DomainProvider } from './contexts/DomainContext'
import { UserInitProvider, useUserInit } from './contexts/UserInitContext'
import { ServiceStatusProvider, useServiceStatus } from './contexts/ServiceStatusContext'

// Providers
import { ApiProvider } from './providers/ApiProvider'

// Hooks
import { usePendingHubSpotConnection } from './hooks/usePendingHubSpotConnection'
import { usePendingTeamInvite } from './hooks/usePendingTeamInvite'
import { usePendingCheckoutRedirect } from './hooks/usePendingCheckoutRedirect'
import { useVersionCheck } from './hooks/useVersionCheck'
import { usePresence } from './hooks/usePresence'
import { useChiefAccess } from './hooks/useChiefAccess'

/**
 * Route guard for /chief. The page itself decides between the briefing and the
 * landing pitch; this guard only turns away accounts that should not see Chief
 * at all. Renders nothing while access resolves so a permitted user never
 * flash-redirects. The API is gated server-side regardless.
 */
function ChiefOnly({ children }: { children: ReactNode }) {
  const { state, isLoading } = useChiefAccess()
  if (isLoading) return null
  if (state === 'hidden') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: (failureCount, error: Error & { response?: { status?: number } }) => {
        // Don't retry on 4xx client errors (includes 401, 403, 404, 429, etc.)
        if (error?.response?.status && error.response.status >= 400 && error.response.status < 500) {
          return false
        }
        // Allow 3 retries for transient errors (5xx, network issues, etc.)
        return failureCount < 3
      },
      // Exponential backoff: 1s, 2s, 4s for retries (fix for 6.34)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false, // Don't auto-refetch when window regains focus
      refetchOnReconnect: false, // Don't auto-refetch when reconnecting
    },
  },
})

const VERSION_CHECK_APP_ROUTE_PREFIXES = [
  '/admin',
  '/dashboard',
  '/domains',
  '/library',
  '/monitoring',
  '/settings',
  '/team/settings',
  '/ai-analytics/pages',
]

const VERSION_CHECK_APP_EXACT_ROUTES = [
  '/hubspot',
  '/model-tester',
  '/roadmap',
  '/whats-new',
]

export function shouldShowVersionCheck(pathname: string, isSignedIn: boolean | undefined): boolean {
  if (!isSignedIn) return false
  if (VERSION_CHECK_APP_EXACT_ROUTES.includes(pathname)) return true
  return VERSION_CHECK_APP_ROUTE_PREFIXES.some(prefix =>
    pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

function AppContent() {
  const location = useLocation()
  // Auto-claim pending HubSpot connections after signup (marketplace-first flow)
  usePendingHubSpotConnection()

  // Auto-accept pending team invitations after OAuth authentication
  usePendingTeamInvite()

  // Redirect to /pricing if user signed up with a pending tier checkout
  usePendingCheckoutRedirect()

  // Track user presence for real-time active user monitoring
  usePresence()

  // Check if Clerk auth is loaded
  const { isLoaded, isSignedIn } = useAuth()

  // Check for new app versions every 30 minutes on authenticated app routes only.
  const showAuthenticatedServiceStatusUi = shouldShowVersionCheck(location.pathname, isSignedIn)
  useVersionCheck(showAuthenticatedServiceStatusUi)

  // Get network issue state from user init context
  const { hasNetworkIssue, dismissNetworkIssue, retryInit } = useUserInit()

  // Get service status to avoid showing network modal when we know services are down
  const { overall: serviceStatus } = useServiceStatus()

  // Show loading screen while Clerk initializes
  // This prevents flash of Welcome page for authenticated users
  if (!isLoaded) {
    return <AuthLoadingScreen />
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Default SEO tags for social sharing - ensures featured image on all pages */}
      <DefaultSEO />

      {/* Network troubleshooting modal - shows when client-side connectivity issues are detected
          Don't show if we know services are down (to avoid misleading "it's your browser" message) */}
      <NetworkTroubleshootingModal
        isOpen={hasNetworkIssue && serviceStatus === 'healthy'}
        onClose={dismissNetworkIssue}
        onRetry={retryInit}
      />

      {/* Service recovery toast - shows when services come back online */}
      {showAuthenticatedServiceStatusUi && <ServiceRecoveryToast />}

      <ChunkErrorBoundary>
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
          {/* Public routes - with tracking pixel */}
          <Route path="/" element={<PublicLayout><HomePage1 /></PublicLayout>} />
          {/* Isolated design-system surfaces: unlinked and noindex until adopted */}
          <Route path="/design-system" element={<DesignSystemPage />} />
          <Route path="/design-system/specimen" element={<SpecimenPage />} />
          <Route path="/ui-kit" element={<UiKitPage />} />
          {/*
            /pricing is not a React route. It is served from the Astro build,
            which is the converted page; the React one was a 695-line stale
            duplicate, and because React Router owned the path it was what
            every in-app link rendered while a hard load got the real thing.

            Links to it are plain anchors now so they leave the SPA
            directly. This catches whatever still navigates client-side, a
            <Navigate> or a ButtonLink, and hands it off with a real
            navigation. `AstroRedirect` is the component /docs already used
            for exactly this, and it is the one to use: it skips the hand-off
            in dev, where there is no Astro server to land on and a redirect
            to the same URL would loop through the SPA forever.
          */}
          <Route path="/pricing" element={<AstroRedirect to="/pricing" />} />
          <Route path="/audit" element={<PublicLayout><Suspense fallback={<div className="min-h-screen" />}><AIAuditLandingPage /></Suspense></PublicLayout>} />
          <Route path="/ai-clarity-audit" element={<Navigate to="/audit" replace />} />
          <Route path="/ai-clarity-report" element={<Navigate to="/audit" replace />} />
          <Route path="/sign-in/*" element={<PublicLayout><SignInPage /></PublicLayout>} />
          <Route path="/sign-up/*" element={<PublicLayout><SignUpPage /></PublicLayout>} />
          <Route path="/checkout/subscription" element={<SubscriptionCheckoutPage />} />
          <Route path="/scan/:sessionId" element={<PublicLayout><PublicScanPage /></PublicLayout>} />
          <Route path="/onboarding/claim" element={<PublicLayout><OnboardingClaimPage /></PublicLayout>} />
          <Route path="/welcome" element={<Navigate to="/" replace />} />
          <Route path="/welcome/intent" element={<PublicLayout><WelcomeIntentPage /></PublicLayout>} />
          <Route path="/welcome/start" element={<PublicLayout><WelcomeStartPage /></PublicLayout>} />
          <Route path="/welcome/reading/:sessionId" element={<PublicLayout><WelcomeReadingPage /></PublicLayout>} />
          <Route path="/welcome/results" element={<PublicLayout><WelcomeResultsPage /></PublicLayout>} />
          <Route path="/welcome/put-live" element={<PublicLayout><WelcomePutLivePage /></PublicLayout>} />
          <Route path="/thank-you" element={<PublicLayout><ThankYouPage /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><TermsOfServicePage /></PublicLayout>} />
          <Route path="/privacy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
          <Route path="/cookies" element={<PublicLayout><CookiesPage /></PublicLayout>} />
          {/* /docs is served by the Astro knowledge base; redirect in-app nav to it. */}
          <Route path="/docs" element={<AstroRedirect to="/docs" />} />
          <Route path="/docs/api" element={<PublicLayout><ApiDocsPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
          <Route path="/methodology" element={<PublicLayout><MethodologyPage /></PublicLayout>} />
          <Route path="/aro" element={<PublicLayout><AROPage /></PublicLayout>} />
          <Route path="/agency" element={<PublicLayout><AgencyPage /></PublicLayout>} />
          <Route path="/managed" element={<PublicLayout><ManagedPage /></PublicLayout>} />
          <Route path="/what-ai-agents-see" element={<PublicLayout><WhatAIAgentsSeePage /></PublicLayout>} />
          <Route path="/agent-readiness-course" element={<PublicLayout><AgentReadinessCoursePage /></PublicLayout>} />
          <Route path="/team-workflow" element={<PublicLayout><TeamWorkflowPage /></PublicLayout>} />
          <Route path="/integrations" element={<PublicLayout><IntegrationsPage /></PublicLayout>} />
          <Route path="/hubspot-aeo" element={<PublicLayout><HubSpotAeoPage /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
          <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
          <Route path="/customers" element={<PublicLayout><CustomersPage /></PublicLayout>} />
          <Route path="/partners" element={<PublicLayout><PartnersPage /></PublicLayout>} />
          <Route path="/press" element={<PublicLayout><PressPage /></PublicLayout>} />
          <Route path="/security" element={<PublicLayout><SecurityPage /></PublicLayout>} />
          <Route path="/sitemap" element={<PublicLayout><SitemapPage /></PublicLayout>} />
          <Route path="/support" element={<PublicLayout><SupportPage /></PublicLayout>} />
          <Route path="/solutions/:solutionSlug" element={<PublicLayout><SolutionIndustryPage /></PublicLayout>} />
          <Route path="/enterprise" element={<Navigate to="/solutions/enterprise" replace />} />
          <Route path="/free-schema-generator" element={<PublicLayout><FreeSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/faq-schema-generator" element={<PublicLayout><FAQSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/article-schema-generator" element={<PublicLayout><ArticleSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/blogposting-schema-generator" element={<PublicLayout><BlogPostingSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/howto-schema-generator" element={<PublicLayout><HowToSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/product-schema-generator" element={<PublicLayout><ProductSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/localbusiness-schema-generator" element={<PublicLayout><LocalBusinessSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/organization-schema-generator" element={<PublicLayout><OrganizationSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/event-schema-generator" element={<PublicLayout><EventSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/review-schema-generator" element={<PublicLayout><ReviewSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/breadcrumb-schema-generator" element={<PublicLayout><BreadcrumbSchemaGeneratorPage /></PublicLayout>} />
          <Route path="/service-schema-generator" element={<PublicLayout><ServiceSchemaGeneratorPage /></PublicLayout>} />

          {/* Resources - Public pillar pages */}
          <Route path="/aeo" element={<AstroRedirect to="/aeo" />} />
          <Route path="/aeo/how-to-win-featured-snippets" element={<PublicLayout><HowToWinFeaturedSnippetsPage /></PublicLayout>} />
          <Route path="/geo" element={<AstroRedirect to="/geo" />} />
          <Route path="/geo/entity-based-seo-strategies" element={<PublicLayout><EntityBasedSeoStrategiesPage /></PublicLayout>} />
          <Route path="/ai-search-optimization" element={<AstroRedirect to="/ai-search-optimization" />} />
          <Route path="/ai-search-optimization/how-ai-engines-rank-sources" element={<PublicLayout><HowAIEnginesRankSourcesPage /></PublicLayout>} />
          <Route path="/schema-markup" element={<AstroRedirect to="/schema-markup" />} />
          <Route path="/schema-markup/json-ld-vs-microdata" element={<PublicLayout><JsonLdVsMicrodataPage /></PublicLayout>} />
          <Route path="/schema-markup/improve-quality-score" element={<AstroRedirect to="/schema-markup/improve-quality-score" />} />
          <Route path="/schema-markup/service-schema" element={<AstroRedirect to="/schema-markup/service-schema" />} />

          {/* The HubSpot AEO articles: static Astro pages from astro/src/articles */}
          <Route path="/hubspot-aeo/guide" element={<AstroRedirect to="/hubspot-aeo/guide" />} />
          <Route path="/hubspot-aeo/structured-data-on-hubspot" element={<AstroRedirect to="/hubspot-aeo/structured-data-on-hubspot" />} />
          <Route path="/hubspot-aeo/organization-schema" element={<AstroRedirect to="/hubspot-aeo/organization-schema" />} />
          <Route path="/hubspot-aeo/schema-validation" element={<AstroRedirect to="/hubspot-aeo/schema-validation" />} />
          <Route path="/hubspot-aeo/recommendations" element={<AstroRedirect to="/hubspot-aeo/recommendations" />} />
          <Route path="/hubspot-aeo/for-agencies" element={<AstroRedirect to="/hubspot-aeo/for-agencies" />} />
          <Route path="/hubspot-aeo/ai-bot-tracking" element={<AstroRedirect to="/hubspot-aeo/ai-bot-tracking" />} />
          <Route path="/hubspot-aeo/llms-txt" element={<AstroRedirect to="/hubspot-aeo/llms-txt" />} />
          <Route path="/hubspot-aeo/measuring-ai-visibility" element={<AstroRedirect to="/hubspot-aeo/measuring-ai-visibility" />} />

          {/* The second ten (package 2026-10-20), same static Astro pages */}
          <Route path="/hubspot-aeo/hubspot-aeo-audit-checklist" element={<AstroRedirect to="/hubspot-aeo/hubspot-aeo-audit-checklist" />} />
          <Route path="/hubspot-aeo/hubspot-service-page-content-template" element={<AstroRedirect to="/hubspot-aeo/hubspot-service-page-content-template" />} />
          <Route path="/hubspot-aeo/hubspot-aeo-project-scope" element={<AstroRedirect to="/hubspot-aeo/hubspot-aeo-project-scope" />} />
          <Route path="/hubspot-aeo/business-information-consistency-for-aeo" element={<AstroRedirect to="/hubspot-aeo/business-information-consistency-for-aeo" />} />
          <Route path="/hubspot-aeo/hubspot-pricing-page-content-template" element={<AstroRedirect to="/hubspot-aeo/hubspot-pricing-page-content-template" />} />
          <Route path="/hubspot-aeo/hubspot-aeo-implementation-costs" element={<AstroRedirect to="/hubspot-aeo/hubspot-aeo-implementation-costs" />} />
          <Route path="/hubspot-aeo/duplicate-schema-markup-in-hubspot" element={<AstroRedirect to="/hubspot-aeo/duplicate-schema-markup-in-hubspot" />} />
          <Route path="/hubspot-aeo/business-knowledge-graph-for-hubspot" element={<AstroRedirect to="/hubspot-aeo/business-knowledge-graph-for-hubspot" />} />
          <Route path="/schema-markup/location-schema-vs-service-area-schema" element={<AstroRedirect to="/schema-markup/location-schema-vs-service-area-schema" />} />
          <Route path="/hubspot-aeo/hubspot-schema-maintenance" element={<AstroRedirect to="/hubspot-aeo/hubspot-schema-maintenance" />} />

          {/* Resources / Blog */}
          <Route path="/resources" element={<PublicLayout><ResourcesListingPage /></PublicLayout>} />
          <Route path="/resources/:slug" element={<PublicLayout><ResourcePostPage /></PublicLayout>} />
          <Route path="/learn" element={<PublicLayout><LearnPage /></PublicLayout>} />
          <Route path="/learn/category/:slug" element={<PublicLayout><LearnCategoryPage /></PublicLayout>} />
          <Route path="/learn/:slug" element={<PublicLayout><LearnVideoPage /></PublicLayout>} />

          {/* Free Tools */}
          <Route path="/schema-markup-grader" element={<PublicLayout><SchemaMarkupGraderPage /></PublicLayout>} />

          {/* Generate — redirects to dashboard (generation is now domain-scoped) */}
          <Route path="/generate" element={<Navigate to="/dashboard" replace />} />
          <Route path="/generate/defaults" element={<Navigate to="/dashboard" replace />} />

          {/* Domains redirect — domain list is now the dashboard */}
          <Route path="/domains" element={<Navigate to="/dashboard" replace />} />

          {/* Domain Detail - Single domain hub page (uses domain name as slug) */}
          <Route path="/domains/:domainSlug" element={
            <Layout>
              <DomainDetailPage />
            </Layout>
          } />

          {/* Domain AI Recommendations - Citation score and provider answer details */}
          <Route path="/domains/:domainSlug/ai-recommendations" element={
            <Layout>
              <DomainDetailPage citationDetailOnly />
            </Layout>
          } />

          {/* Domain AI Report - AI Visibility report within domain context */}
          <Route path="/domains/:domainSlug/ai-report" element={
            <Layout>
              <DomainAIReportPage />
            </Layout>
          } />

          {/* Domain Generate - Schema generation within domain context (legacy) */}
          <Route path="/domains/:domainSlug/generate" element={
            <Layout>
              <DomainGeneratePage />
            </Layout>
          } />

          {/* Monitor Setup - 3-step wizard entered from "Monitor this URL" */}
          <Route path="/domains/:domainSlug/monitor-setup" element={
            <Layout>
              <MonitorSetupPage />
            </Layout>
          } />

          {/* Monitoring Picker — bulk picker for legacy customers
              opting verified URLs into active monitoring. Single
              team-wide route, not domain-scoped — the picker spans
              every domain. */}
          <Route path="/monitoring/select" element={
            <Layout>
              <MonitoringPickerPage />
            </Layout>
          } />

          {/* Domain Bot Tracking - Bot tracking report within domain context */}
          <Route path="/domains/:domainSlug/bot-tracking" element={
            <Layout>
              <DomainBotTrackingPage />
            </Layout>
          } />

          {/* Domain Monitoring - Drift monitoring and continuous intelligence */}
          <Route path="/domains/:domainSlug/monitoring" element={
            <Layout>
              <DomainMonitoringPage />
            </Layout>
          } />

          {/* Domain Settings - Organization defaults and domain configuration */}
          <Route path="/domains/:domainSlug/settings" element={
            <Layout>
              <DomainSettingsPage />
            </Layout>
          } />

          {/* Unified Page Detail - Single page view within a domain */}
          <Route path="/domains/:domainSlug/pages/*" element={
            <Layout>
              <UnifiedPageDetail />
            </Layout>
          } />

          {/* Library redirect — now part of the domain list dashboard */}
          <Route path="/library" element={<Navigate to="/dashboard" replace />} />

          {/* HubSpot - Standalone route at root level */}
          <Route path="/hubspot" element={
            <Layout>
              <HubSpotPage />
            </Layout>
          } />

          {/* HubSpot OAuth Callback - No layout needed */}
          <Route path="/hubspot/callback" element={<HubSpotCallbackPage />} />

          {/* Connect from the onboarding report, with a way back to it */}
          <Route path="/hubspot/connect" element={<HubSpotConnectStartPage />} />

          {/* HubSpot Marketplace Install - No layout needed */}
          <Route path="/hubspot/install" element={<HubSpotInstallPage />} />

          {/* Chief of Staff - admins and entitled pilot accounts (API is gated server-side) */}
          <Route path="/chief" element={
            <Layout>
              <ChiefOnly>
                <ChiefOfStaffPage />
              </ChiefOnly>
            </Layout>
          } />

          {/* Recommendation Report - admins and entitled pilot accounts (API is gated server-side) */}
          <Route path="/chief/recommendations" element={
            <Layout>
              <ChiefOnly>
                <RecommendationReportPage />
              </ChiefOnly>
            </Layout>
          } />

          {/* AI Analytics — redirects to domain list dashboard */}
          <Route path="/ai-analytics" element={<Navigate to="/dashboard" replace />} />
          <Route path="/ai-analytics/site-score" element={<Navigate to="/dashboard" replace />} />
          <Route path="/ai-analytics/bot-tracking" element={<Navigate to="/dashboard" replace />} />

          {/* Page Detail - Standalone route for individual page analysis (keep for bookmarks) */}
          <Route path="/ai-analytics/pages/:shortId" element={
            <Layout>
              <PageDetailPage />
            </Layout>
          } />

          {/* Redirect old /ai-visibility URLs to dashboard */}
          <Route path="/ai-visibility" element={<Navigate to="/dashboard" replace />} />

          {/* What's New - Standalone route at root level */}
          <Route path="/whats-new" element={
            <Layout>
              <WhatsNewPage />
            </Layout>
          } />

          {/* Roadmap - Standalone route at root level */}
          <Route path="/roadmap" element={
            <Layout>
              <RoadmapPage />
            </Layout>
          } />

          {/* Pro redirect — upgrade options now on pricing page */}
          <Route path="/pro" element={<Navigate to="/pricing" replace />} />

          {/* Settings - Nested routes with tabs */}
          <Route path="/settings" element={
            <Layout>
              <SettingsLayout />
            </Layout>
          }>
            <Route index element={<Navigate to="/settings/account" replace />} />
            <Route path="account" element={<AccountSettings />} />
            <Route path="team" element={<TeamSettings />} />
            <Route path="billing" element={<BillingSettings />} />
            <Route path="integrations" element={<IntegrationsSettings />} />
            <Route path="preferences" element={<PreferencesSettings />} />
            <Route path="advanced" element={<AdvancedSettings />} />
            <Route path="billing/subscribe/:planSlug" element={<SubscribeCheckoutPage />} />
            {/* Legacy redirect: organizations now managed via domain settings */}
            <Route path="organizations" element={<Navigate to="/dashboard" replace />} />
          </Route>

          {/* Team routes - Nested routes with tabs */}
          <Route path="/team/settings" element={
            <Layout>
              <TeamSettingsLayout />
            </Layout>
          }>
            <Route index element={<TeamOverviewPage />} />
            <Route path="members" element={<TeamMembersPage />} />
            <Route path="invitations" element={<InvitationsPage />} />
          </Route>

          <Route path="/team/join/:token" element={<JoinTeamPage />} />

          {/* Protected routes - Temporarily bypassed for testing */}
          <Route path="/dashboard/*" element={
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/credits" element={<Navigate to="/dashboard" replace />} />
                <Route path="/settings" element={<Navigate to="/settings" replace />} />
                <Route path="/model-tester" element={<ModelTester />} />
              </Routes>
            </Layout>
          } />

          {/* Model Tester - Standalone route for easy access */}
          <Route path="/model-tester" element={
            <Layout>
              <ModelTester />
            </Layout>
          } />

          {/* Admin - Nested routes with tabs, wrapped in main Layout for sidebar */}
          <Route path="/admin" element={
            <Layout>
              <AdminLayout />
            </Layout>
          }>
            <Route index element={<Navigate to="/admin/analytics" replace />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="growth" element={<AdminGrowth />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="emails" element={<AdminEmails />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="monitoring" element={<AdminMonitoring />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="demo-requests" element={<AdminDemoRequests />} />
            <Route path="agency-applications" element={<AdminAgencyApplications />} />
            <Route path="managed-applications" element={<AdminManagedApplications />} />
            <Route path="cancellations" element={<AdminCancellations />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="roadmap" element={<AdminRoadmap />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="beta-requests" element={<AdminPrivateBetaRequests />} />
            <Route path="chief-beta" element={<AdminChiefBeta />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="overhead" element={<Navigate to="/admin/revenue" replace />} />
            <Route path="audits" element={<AdminAudits />} />
          </Route>

          {/* Fallback route */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ChunkErrorBoundary>
    </div>
  )
}

function AuthCacheBoundary() {
  const { isLoaded, isSignedIn, userId } = useAuth()
  const queryClient = useQueryClient()
  const previousIdentityRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (!isLoaded) return

    const currentIdentity = isSignedIn ? userId || null : null
    const previousIdentity = previousIdentityRef.current

    if (previousIdentity !== undefined && previousIdentity !== currentIdentity) {
      queryClient.clear()
    }

    previousIdentityRef.current = currentIdentity
  }, [isLoaded, isSignedIn, queryClient, userId])

  return null
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthCacheBoundary />
        <ApiProvider>
          <ServiceStatusProvider>
            <UserInitProvider>
              <TeamProvider>
                <DomainProvider>
                <OnboardingProvider>
                  <ScrollToTop />
                  <AppContent />
                  {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
                </OnboardingProvider>
                </DomainProvider>
              </TeamProvider>
            </UserInitProvider>
          </ServiceStatusProvider>
        </ApiProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

export default App
